import invariant from 'invariant';
import camelCase from 'utils/camelCase';

/**
 * Shortcut to compute element style
 *
 * @param {HTMLElement} elem
 * @param {string} prop
 * @returns {string}
 */
export function getStyle(elem: HTMLElement, prop: string): string {
  return window.getComputedStyle(elem, null).getPropertyValue(prop);
}

export function getOffset(elem: null | HTMLElement | Text): { top: number; left: number } {
  invariant(document.documentElement, 'null document.documentElement in getOffset');
  invariant(elem, 'null elem param in getOffset');
  const docElem = document.documentElement;

  let box = { top: 0, left: 0 };

  // If we don't have gBCR, just use 0,0 rather than error
  // BlackBerry 5, iOS 3 (original iPhone)
  if (isHTMLElement(elem)) {
    box = elem.getBoundingClientRect();
  }

  const scrollPosition = getScrollPosition();

  return {
    top: box.top + scrollPosition.top - docElem.clientTop,
    left: box.left + scrollPosition.left - docElem.clientLeft,
  };
}

/**
 * Get elements position
 *
 * @param {HTMLElement} elem
 * @param {HTMLElement?} offsetParent
 * @returns {{top: number, left: number}}
 */
export function getPosition(
  elem: HTMLElement,
  offsetParent: HTMLElement
): { top: number; left: number } {
  let offset;
  let parentOffset = { top: 0, left: 0 };

  // Fixed elements are offset from window (parentOffset = {top:0, left: 0}, because it is its only
  // offset parent
  if (getStyle(elem, 'position') === 'fixed') {
    // We assume that getBoundingClientRect is available when computed position is fixed
    offset = elem.getBoundingClientRect();
  } else {
    if (!offsetParent) {
      // Get *real* offsetParent
      offsetParent = getOffsetParent(elem); // eslint-disable-line no-param-reassign
    }

    // Get correct offsets
    offset = getOffset(elem);
    if (offsetParent.nodeName !== 'HTML') {
      parentOffset = getOffset(offsetParent);
    }

    // Add offsetParent borders
    parentOffset.top += parseInt(getStyle(offsetParent, 'border-top-width'), 10);
    parentOffset.left += parseInt(getStyle(offsetParent, 'border-left-width'), 10);
  }

  // Subtract parent offsets and element margins
  return {
    top: offset.top - parentOffset.top - parseInt(getStyle(elem, 'margin-top'), 10),
    left: offset.left - parentOffset.left - parseInt(getStyle(elem, 'margin-left'), 10),
  };
}

export function getOffsetParent(elem: HTMLElement): HTMLElement {
  invariant(document.documentElement, 'null document.documentElement in getOffsetParent');
  const docElem = document.documentElement;
  let offsetParent = (elem.offsetParent as HTMLElement) || docElem;

  while (
    offsetParent &&
    offsetParent.nodeName !== 'HTML' && getStyle(offsetParent, 'position') === 'static'
  ) {
    offsetParent = offsetParent.offsetParent as HTMLElement;
  }

  return offsetParent || docElem;
}

/**
 * Get scroll position of an element (or document, if no element is provided)
 */
export function getScrollPosition(elem?: HTMLElement): { top: number; left: number } {
  invariant(document.documentElement, 'null document.documentElement in getScrollPosition');
  const docElem = document.documentElement;
  if (!elem || elem === document.body || (elem as any) === window) {
    elem = docElem; // eslint-disable-line no-param-reassign
  }
  return {
    left: elem === docElem ? window.pageXOffset || docElem.scrollLeft : elem.scrollLeft,
    top: elem === docElem ? window.pageYOffset || docElem.scrollTop : elem.scrollTop,
  };
}

/** Typescript Typegaurd */
function isHTMLElement(e: null | HTMLElement | Text): e is HTMLElement {
  return e != null && typeof (e as HTMLElement).getBoundingClientRect === 'function';
}

export default {
  getStyle,
  getOffset,
  getPosition,
  getOffsetParent,
  getScrollPosition,
};
